<template>
    <div class="company-list-container">
        <div class="company-list-header">
            <div class="d-flex align-center">
                <v-icon color="var(--zsh-primary)" class="mr-2">mdi-domain</v-icon>
                <span class="text-h6">{{ title }}</span>
            </div>

            <!-- Search input -->
            <v-text-field
                v-model="searchQuery"
                :placeholder="searchLabel"
                variant="outlined"
                density="compact"
                prepend-inner-icon="mdi-magnify"
                hide-details
                class="mt-2 messages-search-input"
                bg-color="white"
                persistent-placeholder
            />
        </div>

        <div class="company-list-content">
            <!-- Loading state -->
            <div v-if="isLoading" class="d-flex align-center justify-center py-5">
                <v-progress-circular indeterminate color="var(--zsh-primary)" class="mr-3" />
                <span>Loading companies...</span>
            </div>

            <!-- Error state -->
            <v-alert v-else-if="error" type="error" class="mt-3">
                {{ error }}
            </v-alert>

            <!-- Empty state -->
            <div v-else-if="filteredCompanies.length === 0" class="text-center py-5">
                <v-icon color="var(--zsh-primary)" size="48">mdi-domain-off</v-icon>
                <div class="mt-2">No companies found</div>
            </div>

            <!-- Companies list -->
            <v-list v-else density="compact" bg-color="transparent" class="companies-scroll-list">
                <v-list-item
                    v-for="company in filteredCompanies"
                    :key="company.id"
                    :active="selectedCompanyId === company.id"
                    :class="{'selected-item': selectedCompanyId === company.id}"
                    class="mb-3 rounded pa-2"
                    :title="company.name"
                    :subtitle="company.company_id || ''"
                    @click="selectCompany(company)"
                >
                    <template v-slot:prepend>
                        <v-avatar size="32" color="teal-lighten-4" class="mr-2">
                            <v-icon color="teal-darken-2">mdi-domain</v-icon>
                        </v-avatar>
                    </template>
                </v-list-item>
            </v-list>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch, Emit} from 'vue-facing-decorator'

    interface Company {
        id: string
        name: string
        company_id?: string
    }

    @Component
    export default class CompanyList extends Vue {
        @Prop({default: 'Companies'}) title!: string
        @Prop({default: 'Search companies'}) searchLabel!: string
        @Prop({default: () => []}) companies!: Company[]
        @Prop({default: false}) isLoading!: boolean
        @Prop({default: null}) error!: string | null
        @Prop({default: null}) selectedCompanyId!: string | null

        searchQuery: string = ''

        get filteredCompanies(): Company[] {
            if (!this.searchQuery) {
                return this.companies
            }

            const query = this.searchQuery.toLowerCase()
            return this.companies.filter(
                company =>
                    company.name.toLowerCase().includes(query) ||
                    (company.company_id && company.company_id.toLowerCase().includes(query))
            )
        }

        @Emit('company-selected')
        selectCompany(company: Company): Company {
            return company
        }
    }
</script>

<style scoped>
    .company-list-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: var(--zsh-messages-panel-bg);
        border-radius: 4px;
        color: var(--zsh-messages-text);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border: 1px solid var(--zsh-messages-border);
        flex: 1;
    }

    .company-list-header {
        flex: 0 0 auto;
        height: 120px;
        padding: 16px 16px 8px 16px;
        background-color: var(--zsh-primary);
        color: white;
        border-radius: 4px 4px 0 0;
    }

    .company-list-content {
        flex: 1 1 auto;
        overflow: hidden;
        padding: 0 16px 16px 16px;
        display: flex;
        flex-direction: column;
        margin-top: 8px; /* Add space after header */
        min-height: 0; /* Crucial for flex items to respect parent container boundaries */
    }

    .companies-scroll-list {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 100%; /* Use relative height instead of viewport-based calculation */
    }

    .v-list-item {
        transition: background-color 0.2s ease;
        border-radius: 4px;
        margin-bottom: 4px;
    }

    .v-list-item:hover {
        background-color: rgba(0, 0, 0, 0.03);
    }

    .selected-item {
        background-color: var(--zsh-primary) !important;
        color: white !important;
        font-weight: bold;
    }

    .selected-item .v-list-item-subtitle {
        color: rgba(255, 255, 255, 0.8) !important;
    }

    /* Disable label animations and transitions for search inputs */
    .messages-search-input {
        .v-label {
            display: none !important;
        }

        .v-field__label {
            display: none !important;
        }

        .v-field--focused .v-label,
        .v-field--focused .v-field__label {
            display: none !important;
            transform: none !important;
            transition: none !important;
            animation: none !important;
        }

        .v-field__input {
            transition: none !important;
            animation: none !important;
        }

        .v-field {
            transition: none !important;
            animation: none !important;
        }

        /* Ensure placeholder stays in place */
        input::placeholder {
            transition: none !important;
            animation: none !important;
            transform: none !important;
            opacity: 0.6 !important;
        }

        /* Remove any floating label behavior */
        .v-field--variant-outlined.v-field--focused .v-field__outline {
            transition: none !important;
        }
    }
</style>
